import { useState, useEffect, useMemo } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import H from '../texts/h/H';
import Input from '../input/Input';
import CurrencyInput from '../input/CurrencyInput';
import { getQuotations, Quotation } from '../../services/QuotationsService';
import { isLocalCurrencyCode } from '../../utils/currencyLabel';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import { useConfig } from '../../context/ConfigContext';

interface AssetSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: (data: {
    assetId: number;
    assetName: string;
    quantity: number;
    quotation: number;
    calculatedAmount: number;
  }) => void;
  currencyCode: string;
  usdQuotation?: number | null;
  initialAssetId?: number | null;
  initialQuantity?: number | null;
  initialQuotation?: number | null;
}

const AssetSelectionModal = ({
  open,
  onClose,
  onAccept,
  currencyCode,
  usdQuotation,
  initialAssetId,
  initialQuantity,
  initialQuotation,
}: AssetSelectionModalProps) => {
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [assets, setAssets] = useState<Quotation[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [quotation, setQuotation] = useState<number | null>(null);

  const isUsd = !isLocalCurrencyCode(currencyCode);

  useEffect(() => {
    if (!open) return;

    setSelectedAssetId(initialAssetId ?? null);
    setQuantity(initialQuantity ?? null);
    setQuotation(initialQuotation ?? null);

    const fetchAssets = async () => {
      setLoadingAssets(true);
      try {
        const response = await getQuotations();
        if (response.success && response.data && Array.isArray(response.data)) {
          setAssets(response.data as Quotation[]);
        } else {
          setAssets([]);
        }
      } catch {
        setAssets([]);
      } finally {
        setLoadingAssets(false);
      }
    };

    fetchAssets();
  }, [open, initialAssetId, initialQuantity, initialQuotation]);

  const selectedAsset = assets.find((a) => a.id === selectedAssetId);

  const handleAssetSelect = (asset: Quotation) => {
    setSelectedAssetId(asset.id);
    const sale = typeof asset.saleValue === 'string' ? parseFloat(asset.saleValue) : asset.saleValue;
    if (sale && !Number.isNaN(sale) && sale > 0) {
      setQuotation(sale);
    }
  };

  const calculatedAmount = useMemo(() => {
    if (!quantity || !quotation || quantity <= 0 || quotation <= 0) return null;
    const gross = quantity * quotation;
    if (isUsd) {
      if (!usdQuotation || usdQuotation <= 0) return null;
      return gross / usdQuotation;
    }
    return gross;
  }, [quantity, quotation, isUsd, usdQuotation]);

  const isAcceptDisabled =
    !selectedAssetId ||
    !quantity ||
    quantity <= 0 ||
    !quotation ||
    quotation <= 0 ||
    (isUsd && (!usdQuotation || usdQuotation <= 0));

  const handleClose = () => {
    onClose();
  };

  const handleAccept = () => {
    if (isAcceptDisabled || !selectedAssetId || calculatedAmount === null) return;
    onAccept({
      assetId: selectedAssetId,
      assetName: selectedAsset?.name ?? '',
      quantity: quantity!,
      quotation: quotation!,
      calculatedAmount,
    });
  };

  const formatAmount = (value: number) =>
    value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (match) return `${match[1]}/${match[2]}/${match[3]}`;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  const currencyLabel = isUsd ? 'USD' : currencyCode || localCurrencyCode;

  const selectedSaleValue = useMemo(() => {
    if (!selectedAsset) return null;
    const sale = typeof selectedAsset.saleValue === 'string' ? parseFloat(selectedAsset.saleValue) : selectedAsset.saleValue;
    return sale && !Number.isNaN(sale) ? sale : null;
  }, [selectedAsset]);

  const titleWithBadge = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
      Cargar monto en otro activo
      <span
        style={{
          backgroundColor: isUsd ? '#1B3A4B' : '#1B3A4B',
          color: '#FFFFFF',
          padding: '3px 14px',
          borderRadius: '14px',
          fontSize: '12px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        Aval en {currencyLabel}
      </span>
    </span>
  );

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={titleWithBadge}
      width="680px"
      primaryText="Aceptar"
      primaryVariant="primary"
      onPrimaryAction={handleAccept}
      primaryActionDisabled={isAcceptDisabled || loadingAssets}
      secondaryText="Cancelar"
      secondaryVariant="contained"
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <P withMargin={false} type="4" variant="neutral" modifier="strong" weight="bold">
            Activo cotizable
          </P>

          {/* Asset selection table */}
          <div style={{ border: '1px solid #D0DCE8', borderRadius: '8px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F5F7FA' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, width: '40px' }}></th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>ACTIVO</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>COTIZACIÓN ACTUAL</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>ÚLT. ACTUALIZACIÓN</th>
                </tr>
              </thead>
              <tbody>
                {loadingAssets ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
                      Cargando activos...
                    </td>
                  </tr>
                ) : assets.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
                      No hay activos disponibles
                    </td>
                  </tr>
                ) : (
                  assets.map((asset) => {
                    const sale = typeof asset.saleValue === 'string' ? parseFloat(asset.saleValue) : asset.saleValue;
                    const isSelected = selectedAssetId === asset.id;
                    return (
                      <tr
                        key={asset.id}
                        onClick={() => handleAssetSelect(asset)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#EBF5FF' : 'transparent',
                          borderTop: '1px solid #E5E7EB',
                        }}
                      >
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <input
                            type="radio"
                            name="asset-radio"
                            checked={isSelected}
                            onChange={() => handleAssetSelect(asset)}
                            style={{ cursor: 'pointer', accentColor: '#1976D2' }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', fontWeight: isSelected ? 600 : 400 }}>
                          {asset.name}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          {sale && !Number.isNaN(sale) ? `${currencyLabel} ${formatAmount(sale)} / unidad` : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6B7280' }}>
                          {formatDate(asset.updatedAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <CurrencyInput
                label={`Cotización del activo (${currencyLabel} / unidad) *`}
                value={quotation}
                onValueChange={setQuotation}
                size="md"
              />
              <P withMargin={false} type="6" variant="neutral" modifier="weak" style={{ marginTop: '4px' }}>
                {selectedSaleValue ? `Actual: ${formatAmount(selectedSaleValue)} · editable` : 'Seleccioná un activo'}
              </P>
            </div>
            <Input
              label="Cantidad *"
              type="number"
              value={quantity?.toString() ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setQuantity(null);
                  return;
                }
                const parsed = parseFloat(raw);
                setQuantity(Number.isNaN(parsed) ? null : parsed);
              }}
              size="md"
              onlyNumbers
            />
          </div>

          {isUsd && (
            <div>
              <Input
                label="Cotización USD (ARS / USD)"
                value={
                  usdQuotation
                    ? usdQuotation.toLocaleString('es-AR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : 'No disponible'
                }
                readOnly
                size="md"
                modifier="weak"
              />
              <P withMargin={false} type="6" variant="neutral" modifier="weak" style={{ marginTop: '4px' }}>
                Vigente del sistema (quotations) — no editable acá
              </P>
            </div>
          )}

          {/* Calculation preview */}
          <Card
            spacing="3"
            style={{
              backgroundColor: '#F0F4F8',
              borderRadius: '8px',
              border: '1px solid #D0DCE8',
              padding: '16px 20px',
            }}
          >
            {quantity && quotation && quantity > 0 && quotation > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '15px', color: '#4B5563' }}>
                    {formatAmount(quantity)} unidades × {localCurrencyCode} {formatAmount(quotation)}
                  </span>
                  <span style={{ fontSize: '15px', color: '#4B5563', fontWeight: 500 }}>
                    {localCurrencyCode} {formatAmount(quantity * quotation)}
                  </span>
                </div>
                {isUsd && usdQuotation && usdQuotation > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '15px', color: '#4B5563' }}>
                      {localCurrencyCode} {formatAmount(quantity * quotation)} ÷ {formatAmount(usdQuotation)} (USD/{localCurrencyCode})
                    </span>
                    <span style={{ fontSize: '15px', color: '#4B5563', fontWeight: 500 }}>
                      USD {formatAmount((quantity * quotation) / usdQuotation)}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '6px',
                    borderTop: '1px solid #D0DCE8',
                    paddingTop: '10px',
                  }}
                >
                  <span style={{ fontSize: '16px', fontWeight: 700, color: '#1F2937' }}>
                    Monto bruto ({currencyLabel})
                  </span>
                  <span style={{ fontSize: '16px', fontWeight: 700, color: '#1F2937' }}>
                    {calculatedAmount !== null
                      ? `${currencyLabel} ${formatAmount(calculatedAmount)}`
                      : '—'}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#9CA3AF' }}>Monto bruto calculado</span>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#9CA3AF' }}>—</span>
              </div>
            )}
          </Card>
        </div>
      </Card>
    </Modal>
  );
};

export default AssetSelectionModal;
